import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { User, Package, LogOut, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useUser, userApi } from '../context/UserContext';
import { useToast } from '../hooks/use-toast';
import usePageSEO from '../hooks/usePageSEO';
import { isStrongPassword } from '../lib/password';

const STATUS_LABELS = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
  confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-700' },
  processing: { label: 'Processing', color: 'bg-purple-100 text-purple-700' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' },
};

const Account = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout, updateProfile, isAuthenticated, loading: authLoading } = useUser();
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [tab, setTab] = useState('orders');
  const [profileForm, setProfileForm] = useState({ name: '', phone: '', password: '' });
  const [trackNumber, setTrackNumber] = useState('');
  const [trackResult, setTrackResult] = useState(null);

  usePageSEO({ title: t('pages.accountTitle'), noIndex: true });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (user) {
      setProfileForm({ name: user.name, phone: user.phone || '', password: '' });
      loadOrders();
    }
  }, [user]);

  const loadOrders = async () => {
    try {
      const response = await userApi.get('/orders');
      setOrders(response.data);
    } catch { }
    finally { setLoadingOrders(false); }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      const data = { name: profileForm.name, phone: profileForm.phone };
      if (profileForm.password) {
        if (!isStrongPassword(profileForm.password)) {
          toast({ title: 'Error', description: t('auth.passwordComplexity'), variant: 'destructive' });
          return;
        }
        data.password = profileForm.password;
      }
      await updateProfile(data);
      setProfileForm({ ...profileForm, password: '' });
      toast({ title: 'Updated', description: 'Profile saved successfully' });
    } catch {
      toast({ title: 'Error', description: 'Failed to update profile', variant: 'destructive' });
    }
  };

  const handleTrack = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/track/${trackNumber}`);
      if (!response.ok) throw new Error();
      setTrackResult(await response.json());
    } catch {
      toast({ title: 'Not Found', description: 'Order number not found', variant: 'destructive' });
      setTrackResult(null);
    }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  if (authLoading) return <div className="min-h-screen pt-24 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div></div>;
  if (!user) return null;

  const tabs = [
    { key: 'orders', label: t('pages.myOrders'), icon: Package },
    { key: 'track', label: t('pages.trackOrder'), icon: Search },
    { key: 'profile', label: t('pages.profile'), icon: User }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gray-50" data-testid="account-page">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('pages.accountTitle')}</h1>
            <p className="text-gray-600">{t('pages.welcome')} {user.name}</p>
          </div>
          <Button variant="outline" onClick={handleLogout} data-testid="logout-btn"><LogOut className="h-4 w-4 mr-2" />{t('nav.login') === 'تسجيل الدخول' ? 'خروج' : 'Logout'}</Button>
        </div>

        <div className="flex gap-2 mb-6">
          {tabs.map(tb => (
            <Button key={tb.key} variant={tab === tb.key ? 'default' : 'outline'} className={tab === tb.key ? 'bg-red-600 hover:bg-red-700' : ''} onClick={() => setTab(tb.key)}>
              <tb.icon className="h-4 w-4 mr-2" />{tb.label}
            </Button>
          ))}
        </div>

        {tab === 'orders' && (
          <div>
            {loadingOrders ? (
              <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div></div>
            ) : orders.length === 0 ? (
              <Card><CardContent className="p-12 text-center text-gray-500">
                <Package className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg mb-2">{t('pages.noOrders')}</p>
                <Button className="bg-red-600 hover:bg-red-700" asChild><Link to="/shop">{t('pages.startShopping')}</Link></Button>
              </CardContent></Card>
            ) : (
              <div className="space-y-4">
                {orders.map(order => (
                  <Card key={order.id} data-testid={`user-order-${order.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <span className="font-mono font-bold">{order.order_number}</span>
                          <span className="text-sm text-gray-500 ml-3">{new Date(order.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="flex gap-2">
                          <Badge className={STATUS_LABELS[order.status]?.color}>{STATUS_LABELS[order.status]?.label}</Badge>
                          <Badge className={order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>{order.payment_status}</Badge>
                        </div>
                      </div>
                      <div className="border-t pt-3">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm py-1">
                            <span>{item.product_name} <span className="text-gray-400">x{item.quantity}</span></span>
                            <span className="font-semibold">{(item.price * item.quantity).toFixed(3)} BHD</span>
                          </div>
                        ))}
                        <div className="flex justify-between font-bold mt-2 pt-2 border-t">
                          <span>{t('pages.total')}</span>
                          <span className="text-red-600">{order.total.toFixed(3)} BHD</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'track' && (
          <Card>
            <CardHeader><CardTitle>{t('pages.trackOrder')}</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleTrack} className="flex gap-3 mb-6">
                <Input value={trackNumber} onChange={(e) => setTrackNumber(e.target.value)} placeholder={t('pages.enterOrderNumber')} required data-testid="track-order-input" className="flex-1" />
                <Button type="submit" className="bg-red-600 hover:bg-red-700" data-testid="track-order-btn">{t('pages.trackOrder')}</Button>
              </form>
              {trackResult && (
                <div className="border rounded-lg p-4 space-y-3" data-testid="track-result">
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-bold text-lg">{trackResult.order_number}</span>
                    <Badge className={STATUS_LABELS[trackResult.status]?.color}>{STATUS_LABELS[trackResult.status]?.label}</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-gray-500">Payment:</span> <Badge className={trackResult.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>{trackResult.payment_status}</Badge></div>
                    <div><span className="text-gray-500">{t('pages.total')}:</span> <span className="font-bold text-red-600">{trackResult.total.toFixed(3)} BHD</span></div>
                  </div>
                  <div className="border-t pt-3">
                    {trackResult.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm py-1">
                        <span>{item.product_name} x{item.quantity}</span>
                        <span>{(item.price * item.quantity).toFixed(3)} BHD</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {tab === 'profile' && (
          <Card>
            <CardHeader><CardTitle>{t('pages.editProfile')}</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleProfileUpdate} className="space-y-4 max-w-md">
                <div><label className="text-sm font-medium">{t('pages.name')}</label><Input value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} data-testid="profile-name" /></div>
                <div><label className="text-sm font-medium">{t('pages.email')}</label><Input value={user.email} disabled className="bg-gray-50" /></div>
                <div><label className="text-sm font-medium">{t('pages.phone')}</label><Input value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} data-testid="profile-phone" /></div>
                <div><label className="text-sm font-medium">{t('pages.newPassword')}</label><Input type="password" value={profileForm.password} onChange={(e) => setProfileForm({ ...profileForm, password: e.target.value })} /></div>
                <Button type="submit" className="bg-red-600 hover:bg-red-700" data-testid="profile-save">{t('pages.saveChanges')}</Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Account;
